import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import { ActionIcon, Menu, Text } from "@mantine/core";
import { IconDots } from "@tabler/icons-react";

export const TRUNCATE_STYLE: CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Block span so the cell's own typography is inherited while we own
// single-line truncation.
const truncatingText = (value: ReactNode): ReactNode => (
  <span style={{ display: "block", ...TRUNCATE_STYLE }}>{value}</span>
);

// These spans copy the library TableCell markup (root > content) so tables render identically.
// They exist because the 0.5.x Table wrapped every cell in a <Text> (<p>), where a <div> was
// re-parented by the browser and crashed React on unmount. 0.6.0 wraps cells in a <div>, so that
// reason is gone and these could be replaced by the library cells outright — tracked separately.
// The horizontal inset is NOT set here: from 0.6.0 the td owns it (12px each side, 8px for
// icon-only cells), and an inline padding would win over the library's rule and be applied twice.
const CELL_ROOT_STYLE: CSSProperties = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  height: 40,
  minHeight: 40,
  width: "100%",
  boxSizing: "border-box",
  overflow: "hidden",
  backgroundColor: "transparent",
  color: "var(--text-default)",
  borderRadius: "var(--radius-0)",
  transition: "background-color 0.15s ease",
};

const CELL_CONTENT_STYLE: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  maxWidth: "100%",
  width: "100%",
  height: "100%",
};

const CONTENT_INLINE_STYLE: CSSProperties = {
  ...CELL_CONTENT_STYLE,
  gap: "var(--spacing-x-small)",
};

const CONTENT_STACKED_STYLE: CSSProperties = {
  ...CELL_CONTENT_STYLE,
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  overflow: "hidden",
};

interface InlineTableCellProps {
  children: ReactNode;
  rootStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  /** Marks the cell so the library's td gives it 8px instead of 12px each side. */
  spacing?: "iconOnly";
}

export function InlineTableCell({
  children,
  rootStyle,
  contentStyle = CONTENT_INLINE_STYLE,
  spacing,
}: InlineTableCellProps) {
  return (
    <span style={{ ...CELL_ROOT_STYLE, ...rootStyle }} data-slot="table-cell-inner">
      <span style={contentStyle} data-slot="table-cell-content" data-spacing={spacing}>
        {children}
      </span>
    </span>
  );
}

// Storybook cell vocabulary (Components/Table): text → spacing="text" + truncated
// Text; badge → spacing="compact". Kept verbatim so every migrated table renders
// identically to the reference stories.
export const renderTextCell = (value: ReactNode) => (
  <InlineTableCell>
    <Text component="span" size="sm" style={TRUNCATE_STYLE}>
      {value}
    </Text>
  </InlineTableCell>
);

// Two-line cell. Both lines stay on one line (truncate rather than wrap).
export const renderTextSubtextCell = (primary: ReactNode, secondary?: ReactNode) => (
  <InlineTableCell contentStyle={CONTENT_STACKED_STYLE}>
    <span
      style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, maxWidth: "100%" }}
    >
      <Text component="span" size="sm" fw={500}>
        {truncatingText(primary)}
      </Text>
      {secondary != null && (
        <Text component="span" size="xs" c="dimmed">
          {truncatingText(secondary)}
        </Text>
      )}
    </span>
  </InlineTableCell>
);

export const renderBadgeCell = (badge: ReactNode) => <InlineTableCell>{badge}</InlineTableCell>;

// Library TableProfileCell markup: avatar leading (dist ._avatarLeading_*) + stacked texts.
const AVATAR_LEADING_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  flex: "0 0 auto",
  marginRight: "var(--spacing-3x-small)",
};

export const renderProfileCell = (
  leading: ReactNode,
  primary: ReactNode,
  secondary?: ReactNode,
) => (
  <InlineTableCell>
    <span style={AVATAR_LEADING_STYLE} data-slot="table-cell-avatar-leading">
      {leading}
    </span>
    <span
      style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, maxWidth: "100%" }}
    >
      <Text component="span" size="sm" fw={500}>
        {truncatingText(primary)}
      </Text>
      {secondary != null && (
        <Text component="span" size="xs" c="dimmed">
          {truncatingText(secondary)}
        </Text>
      )}
    </span>
  </InlineTableCell>
);

// Matches the storybook's Cell-Types / Sticky-Columns "more" action trigger styling.
export const MORE_ACTION_ICON_STYLE = {
  "--ai-color": "var(--neutral-900)",
  "--ai-hover": "var(--bg-neutral-weak)",
  "--ai-hover-color": "var(--neutral-900)",
} as CSSProperties;

// Library TableIconCell (base of TableCheckboxCell/TableMoreCell): spacing="iconOnly"
// (dist ._spacingIconOnly_*) + align="center" (dist ._alignCenter_*).
const ICON_ONLY_ROOT_STYLE: CSSProperties = {
  justifyContent: "center",
  textAlign: "center",
};

export function IconOnlyTableCell({ children }: { children: ReactNode }) {
  return (
    <InlineTableCell
      rootStyle={ICON_ONLY_ROOT_STYLE}
      contentStyle={CONTENT_CENTERED_STYLE}
      spacing="iconOnly"
    >
      {children}
    </InlineTableCell>
  );
}

// The root's justifyContent alone cannot centre anything, because the content span it holds is
// width:100% and absorbs the whole cell — so the centring has to happen inside that span.
const CONTENT_CENTERED_STYLE: CSSProperties = {
  ...CONTENT_INLINE_STYLE,
  justifyContent: "center",
};

// From 0.6.0 the td's inset is symmetric, so the content box is already centred on the column and
// the old right-only padding (which balanced 0.5.2's left-only td) would now push it off centre.
const CHECKBOX_ROOT_STYLE: CSSProperties = {
  justifyContent: "center",
  textAlign: "center",
};

// Library TableCheckboxCell: TableIconCell geometry with the control centred in the column.
export function CheckboxTableCell({ children }: { children: ReactNode }) {
  return (
    <InlineTableCell rootStyle={CHECKBOX_ROOT_STYLE} contentStyle={CONTENT_CENTERED_STYLE}>
      {children}
    </InlineTableCell>
  );
}

export function RowActionsCell({ children }: { children: ReactNode }) {
  return (
    <IconOnlyTableCell>
      <Menu withinPortal shadow="md" position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="subtle" style={MORE_ACTION_ICON_STYLE} aria-label="More actions">
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>{children}</Menu.Dropdown>
      </Menu>
    </IconOnlyTableCell>
  );
}

// The library Checkbox renders Mantine's Checkbox, whose root is a <div> — same
// invalid-in-<p> problem as the cells above (headers wrap labels in a <Text> too).
// Native input styled with the library dist checkbox CSS (._input_ddnj6_* size=small).
export type InlineCheckboxState = "selected" | "deselected" | "intermediate";

const CHECKBOX_WRAPPER_STYLE: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  width: "var(--component-size-3x-small)",
  height: "var(--component-size-3x-small)",
};

const CHECKBOX_INPUT_STYLE: CSSProperties = {
  appearance: "none",
  boxSizing: "border-box",
  width: "100%",
  height: "100%",
  margin: 0,
  padding: 0,
  borderRadius: 4,
  cursor: "pointer",
  transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
};

const CHECKBOX_UNCHECKED_STYLE: CSSProperties = {
  ...CHECKBOX_INPUT_STYLE,
  backgroundColor: "var(--color-white)",
  border: "2px solid var(--icon-weak)",
};

const CHECKBOX_CHECKED_STYLE: CSSProperties = {
  ...CHECKBOX_INPUT_STYLE,
  backgroundColor: "var(--bg-brand-default)",
  border: "1px solid var(--border-brand-default)",
};

const CHECKBOX_ICON_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  margin: "auto",
  pointerEvents: "none",
  color: "var(--text-inverse-default)",
};

interface InlineCheckboxProps {
  state: InlineCheckboxState;
  onValueChange: (checked: boolean) => void;
  onClick?: MouseEventHandler<HTMLInputElement>;
  style?: CSSProperties;
  "aria-label"?: string;
}

export function InlineCheckbox({
  state,
  onValueChange,
  onClick,
  style,
  "aria-label": ariaLabel,
}: InlineCheckboxProps) {
  const checked = state === "selected";
  const indeterminate = state === "intermediate";
  return (
    <span style={{ ...CHECKBOX_WRAPPER_STYLE, ...style }} data-slot="checkbox">
      <input
        type="checkbox"
        checked={checked}
        aria-checked={indeterminate ? "mixed" : checked}
        aria-label={ariaLabel}
        onChange={(event) => onValueChange(event.currentTarget.checked)}
        onClick={onClick}
        style={checked || indeterminate ? CHECKBOX_CHECKED_STYLE : CHECKBOX_UNCHECKED_STYLE}
      />
      {(checked || indeterminate) && (
        <svg
          viewBox="0 0 24 24"
          width={14}
          height={14}
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          style={CHECKBOX_ICON_STYLE}
        >
          {indeterminate ? <path d="M6 12h12" /> : <path d="M5 13l4 4L19 7" />}
        </svg>
      )}
    </span>
  );
}
